import { isLocalCurrency } from '@/utils/isLocalCurrency';

export const SUPPORTED_PAYMENT_METHOD_TYPES = [
  'pix',
  'pix_auto',
  'credit_card',
  'picpay',
  'applepay',
  'googlepay',
  'openfinance_nubank',
  'paypal_wallet',
  'pagaleve',
  // Pagamento dividido. O backend so os lista para o produtor que tem a feature
  // experimental, entao esta lista nao os expoe a quem nao deveria ve-los —
  // ela apenas para de descarta-los quando o backend os manda.
  'multi_card',
  'card_pix',
];

/** Metodos de vitrine: a venda e cobrada em mais de uma transacao, no meio real de cada parte. */
export const MULTIPAYMENT_METHOD_TYPES = ['multi_card', 'card_pix'];

export function isMultipaymentMethodType(type) {
  return Boolean(type && MULTIPAYMENT_METHOD_TYPES.includes(type));
}

/**
 * Piso por cobranca, em reais. Espelha `MIN_PART_AMOUNT_CENTS` no backend e no
 * checkout — divergir aqui so adia a recusa para a hora de pagar.
 */
export const MULTIPAYMENT_MIN_PART_AMOUNT = 5;

/**
 * Piso do produto para que a divisao seja possivel.
 *
 * Duas cobrancas de R$ 5,00 e o menor arranjo valido, entao abaixo de R$ 10,00
 * NAO EXISTE divisao que passe: num produto de R$ 10,00 so 5+5 serve, e num de
 * R$ 9,00 nem isso. Deixar o produtor ligar o metodo nesse caso e prometer ao
 * comprador um caminho que sempre termina em recusa.
 */
export const MULTIPAYMENT_MIN_PRODUCT_AMOUNT = MULTIPAYMENT_MIN_PART_AMOUNT * 2;

/**
 * O menor preco que o produto pode cobrar, considerando as ofertas.
 *
 * E o menor que manda: uma oferta de R$ 7,00 num produto de R$ 100,00 quebraria
 * a divisao so naquela oferta, e o produtor descobriria pelo comprador.
 */
export function lowestProductPrice({ price, offers } = {}) {
  const valores = [price, ...(Array.isArray(offers) ? offers.map((offer) => offer?.price) : [])]
    .map((valor) => Number(valor))
    .filter((valor) => Number.isFinite(valor) && valor > 0);

  return valores.length ? Math.min(...valores) : 0;
}

export const MULTIPAYMENT_MIN_PRODUCT_AMOUNT_MESSAGE =
  'Pagamento dividido disponível apenas para produtos a partir de R$ 10,00: são duas cobranças de no mínimo R$ 5,00 cada.';

/** O produto comporta pagamento dividido? */
export function supportsMultipayment(product) {
  return lowestProductPrice(product) >= MULTIPAYMENT_MIN_PRODUCT_AMOUNT;
}

/**
 * Piso do produto para o Pix 4x sem juros (Pagaleve), em reais. E regra da Pagaleve
 * ("compra a partir de R$ 40,00"), nao nossa: abaixo disso o metodo nem abre no
 * checkout, e deixar o produtor liga-lo e prometer um caminho que nunca aparece.
 */
export const PAGALEVE_MIN_PRODUCT_AMOUNT = 40;

export const PAGALEVE_MIN_PRODUCT_AMOUNT_MESSAGE =
  'Pix 4x sem juros disponível apenas para produtos a partir de R$ 40,00.';

/**
 * O método que a Cakto desligou para a conta no split-admin (CKTPC-1938). O produtor não lê o
 * motivo escrito lá, que é decisão de risco da Cakto: lê só quem desligou e a quem pedir a volta.
 */
export const CAKTO_DISABLED_PAYMENT_METHOD_MESSAGE =
  'Desativado pela Cakto para a sua conta. Fale com o suporte para reativar.';

export const CAKTO_DISABLED_PAYMENT_METHOD_CAPTION = 'Desativado pela Cakto';

/**
 * So o preco do produto principal conta, ao contrario do pagamento dividido, que olha
 * a menor oferta. O checkout-v2 decide por oferta (`PAGALEVE_MIN_OFFER_PRICE`, sobre
 * `offer.price`, sem bump nem taxa), entao uma oferta barata so esconde o metodo nela
 * mesma, nao nas outras; bloquear o produto inteiro pela menor oferta tiraria o metodo
 * das ofertas que passam. O preco principal e o que a tela mostra ao lado do card. O
 * custo da escolha: produto principal abaixo do piso com oferta acima fica sem o metodo.
 */
export function supportsPagaleve({ price } = {}) {
  const valor = Number(price);
  return Number.isFinite(valor) && valor >= PAGALEVE_MIN_PRODUCT_AMOUNT;
}

/** Métodos injetados pelo backend; aparecem em listagens/taxas, mas não na criação/edição de produto. */
export const BACKEND_MANAGED_PAYMENT_METHOD_TYPES = ['mercadopago_wallet'];

/** Métodos exibidos no filtro de vendas apenas para quem tem multimoeda (`hasMultiCurrency`). */
export const MULTICURRENCY_PAYMENT_METHOD_TYPES = [
  'oxxo',
  'spei',
  'debit_card',
  'pse',
  'nequi',
  'safetypay',
  'paypal_wallet',
  'mercadopago_wallet',
];

/**
 * Métodos internacionais só entram na seleção de métodos do produto para quem tem multimoeda
 * (`hasMultiCurrency`). Como `OrderPaymentMethod` é a lista de permitidos dessa seleção, todo
 * método multimoeda que ganha label em vendas precisa estar aqui também, senão vaza para
 * qualquer produtor.
 */
export const FEATURE_FLAGGED_PRODUCT_PAYMENT_METHOD_TYPES = [
  'oxxo',
  'spei',
  'debit_card',
  'pse',
  'nequi',
  'safetypay',
];

/**
 * Métodos que o backend só aceita em produto de moeda estrangeira. Em produto em real o save os
 * recusa pela allowlist da moeda, então oferecê-los na edição de produto BRL é uma opção que
 * nunca salva.
 */
export const FOREIGN_CURRENCY_ONLY_PRODUCT_PAYMENT_METHOD_TYPES = ['debit_card'];

/** Aliases adicionais por tipo canônico (ex.: paypal → paypal_wallet). */
export const PAYMENT_METHOD_ALIASES_BY_CANONICAL = {
  paypal_wallet: ['paypal'],
};

export function isSupportedPaymentMethodType(type) {
  return Boolean(type && SUPPORTED_PAYMENT_METHOD_TYPES.includes(type));
}

export function filterSupportedPaymentMethodTypes(types) {
  if (!Array.isArray(types)) return [];
  return types.filter(isSupportedPaymentMethodType);
}

export function resolvePaymentMethodType(type) {
  if (!type) return type;

  for (const [canonical, aliases] of Object.entries(PAYMENT_METHOD_ALIASES_BY_CANONICAL)) {
    if (canonical === type || aliases.includes(type)) {
      return canonical;
    }
  }

  return type;
}

export function isMulticurrencyPaymentMethodType(type) {
  return Boolean(type && MULTICURRENCY_PAYMENT_METHOD_TYPES.includes(resolvePaymentMethodType(type)));
}

export function isFeatureFlaggedProductPaymentMethodType(type) {
  return Boolean(
    type && FEATURE_FLAGGED_PRODUCT_PAYMENT_METHOD_TYPES.includes(resolvePaymentMethodType(type))
  );
}

export function isProductPaymentMethodTypeVisible(type, hasMultiCurrencyFeature, currency) {
  if (!type) return false;
  if (
    currency !== undefined &&
    isLocalCurrency(currency) &&
    FOREIGN_CURRENCY_ONLY_PRODUCT_PAYMENT_METHOD_TYPES.includes(resolvePaymentMethodType(type))
  ) {
    return false;
  }
  if (!isFeatureFlaggedProductPaymentMethodType(type)) return true;
  return Boolean(hasMultiCurrencyFeature);
}
